<template>
    <section class="tab2-card-section">
        <card-title :module="item" />
        <!-- Tabs -->
        <div v-if="tabs.length > 0" class="tab2-card__tabs">
            <button v-for="(tab, idx) in tabs" :key="idx" class="tab2-card__tab"
                :class="{ 'tab2-card__tab--active': activeTab === idx }" @click="activeTab = idx">
                {{ tab.label }}
            </button>
        </div>
        <component :is="resolveComponent(activeTabData.displayModeCode)" :item="activeTabData" :hideTitle="true" />
    </section>
</template>

<script setup lang="ts">
import { ref, computed, markRaw, type Component } from 'vue'
import type { Module } from '@/api/page_config'
import { CARD_COMPONENT_MAP } from '.';

const props = defineProps<{
    item: Module
}>()

function resolveComponent(modeCode: string): Component {
    const comp = CARD_COMPONENT_MAP[modeCode]
    return comp ? markRaw(comp) : markRaw(CARD_COMPONENT_MAP.Card)
}


interface TabItem {
    label: string
    item: Module
}

const activeTab = ref(0)

const tabs = computed<TabItem[]>(() => {
    return props.item.items?.map((item) => ({
        label: item.mainTitle,
        item: item
    })) || []
})

const activeTabData = computed(() => {
    return tabs.value[activeTab.value]?.item
})

</script>

<style scoped lang="scss">
@use '@/assets/styles/variables' as *;

.tab2-card-section {
    width: 100%;
    max-width: $container-max-width;
    margin: 0 auto;
    padding: 0 1rem;

    @media (min-width: $breakpoint-md) {
        padding: 0 2rem;
    }
}

.tab2-card__tabs {
    display: flex;
    align-items: center;
    background: #f0f0f0;
    border-radius: 8px;
    padding: 4px;
    gap: 2px;
    margin-bottom: 1.5rem;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    justify-content: flex-start;
    width: fit-content;
    max-width: 100%;

    &::-webkit-scrollbar {
        display: none;
    }

    @media (min-width: $breakpoint-md) {
        padding: 5px;
        gap: 4px;
        margin-bottom: 2rem;
    }
}

.tab2-card__tab {
    flex-shrink: 0;
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    color: #1a1a1a;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    white-space: nowrap;
    text-align: center;
    transition: all 0.25s ease;
    font-family: inherit;

    @media (min-width: $breakpoint-md) {
        font-size: 1.125rem;
        padding: 0.625rem 1.5rem;
    }

    &:hover {
        color: #000;
    }

    &--active {
        background: #1a1a1a;
        color: #ffffff;
        font-weight: 700;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);

        &:hover {
            color: #ffffff;
        }

        @media (min-width: $breakpoint-md) {
            font-weight: 800;
        }
    }
}
</style>
